import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from 'Components';
import { useTranslation } from 'react-i18next';
import Heading from 'LearningManager/components/Heading';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import ConfigureSettingsCard from 'LearningManager/components/Review/ConfigureSettingsCard';
import { useStores } from 'Hooks/useStore';
import {
	getAssignUsersSelection,
	options
} from 'LearningManager/constants/createAssignment.constant';

const Review: React.FC = observer(() => {
	const { t } = useTranslation();
	const { learningManagerStore } = useStores();
	const { draft } = useStepper();
	const { configure, customSetup } = draft;

	const { learningPaths, activities } = learningManagerStore;

	// --- Courses section ---
	const selectedLearningPaths = learningPaths.filter((lp) =>
		customSetup.selectedLearningPathCourseIds.includes(lp.id)
	);
	const selectedActivities = activities.filter((a) =>
		customSetup.selectedIndividualCourseIds.includes(a.id)
	);

	// --- Audience section ---
	const selectedOptionId = customSetup.selectedAssignUsersOptionId;
	const audienceLabel = options.find((opt) => opt.id === selectedOptionId)?.title ?? 'learning.createAssignment.review.notSelected';

	const audienceNames = useMemo(
		() =>
			getAssignUsersSelection(draft).map((item) =>
				'firstName' in item
					? `${item.firstName} ${item.lastName}`
					: item.name
			),
		[draft]
	);

	const courseCount =
		selectedLearningPaths.length + selectedActivities.length;

	return (
		<div id="lms-ca-custom-review" className={styles.review}>
			<Heading
				title={t('learning.createAssignment.review.title')}
				subHeading={t('learning.createAssignment.review.customSubHeading')}
			/>

			<div
				id="lms-ca-custom-review-courses"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					{t('learning.createAssignment.review.coursesSelected', {
						count: courseCount
					})}
				</div>

				<div className={styles.courseList}>
					{selectedLearningPaths.map((lp, index) => (
						<div
							key={`lp-${lp.id}`}
							id={`lms-ca-custom-review-lp-${index}`}
							className={styles.courseItem}
						>
							<div className={styles.courseItemLeft}>
								<div className={styles.serial}>{index + 1}</div>
								<Icon type="learningPathOutline" role="img" />

								<div className={styles.courseLabel}>
									{lp.name}
								</div>

								<div className={styles.optionalTag}>
									{customSetup.optionalLearningPathIds.includes(lp.id)
										? t('learning.createAssignment.review.optional')
										: t('learning.createAssignment.review.required')}
								</div>
							</div>
						</div>
					))}

					{selectedActivities.map((activity, index) => (
						<div
							key={`act-${activity.id}`}
							id={`lms-ca-custom-review-act-${index}`}
							className={styles.courseItem}
						>
							<div className={styles.courseItemLeft}>
								<div className={styles.serial}>
									{selectedLearningPaths.length + index + 1}
								</div>

								<Icon type="indiCoursesOutline" role="img" />

								<div className={styles.courseLabel}>
									{activity.name}
								</div>

								<div className={styles.optionalTag}>
									{customSetup.optionalIndividualCourseIds.includes(activity.id)
										? t('learning.createAssignment.review.optional')
										: t('learning.createAssignment.review.required')}
								</div>
							</div>
						</div>
					))}

					{courseCount === 0 && (
						<div className={styles.empty}>
							{t('learning.createAssignment.review.noCoursesSelected')}
						</div>
					)}
				</div>
			</div>

			<div
				id="lms-ca-custom-review-audience"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					{t('learning.createAssignment.review.audience.title')}
				</div>

				<div className={styles.summaryCard}>
					<div>
						<div className={styles.mutedLabel}>
							{t(
								'learning.createAssignment.review.audience.assignedLearners'
							)}
						</div>

						<div className={styles.summaryValue}>
							{t(audienceLabel)}
						</div>
					</div>

					{audienceNames.length > 0 && (
						<div>
							<div className={styles.mutedLabel}>
								{t('learning.createAssignment.review.audience.selected')}
							</div>

							<div className={styles.summaryValue}>
								{audienceNames.join(', ')}
							</div>
						</div>
					)}
				</div>
			</div>

			<div
				id="lms-ca-custom-review-configure"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					{t('learning.createAssignment.review.configureSettings')}
				</div>
				<ConfigureSettingsCard configure={configure} />
			</div>
		</div>
	);
});

export default Review;